import { useState, useEffect, useCallback, useRef, Component } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useNavigate } from "react-router-dom";
import axios from "axios";
import { Toaster } from "@/components/ui/sonner";
import { toast } from "sonner";
import LoginPage from "@/pages/LoginPage";
import PresenterHubPage from "@/pages/PresenterHubPage";
import RacingIntelligencePage from "@/pages/RacingIntelligencePage";
import BroadcastOverlayPage from "@/pages/BroadcastOverlayPage";
import CyclingVisionPage from "@/pages/CyclingVisionPage";
import RaceLibraryPage from "@/pages/RaceLibraryPage";
import SharedDemoPage from "@/pages/SharedDemoPage";
import AutoDemoPage from "@/pages/AutoDemoPage";
import GuidedTourPage from "@/pages/GuidedTourPage";
import DemoAnalyticsPage from "@/pages/DemoAnalyticsPage";
import ScreenProtection from "@/components/ScreenProtection";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;
export const WS_URL = BACKEND_URL.replace('https://', 'wss://').replace('http://', 'ws://');

// =============================================================================
// ERROR BOUNDARY — catch React crashes, display safe fallback
// =============================================================================
class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }
  static getDerivedStateFromError() { return { hasError: true }; }
  componentDidCatch(error, info) { console.error('ErrorBoundary:', error, info); }
  render() {
    if (this.state.hasError) {
      return (
        <div data-testid="error-boundary-fallback" style={{ background: '#121212', color: '#F97316', minHeight: '100vh',
          display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column' }}>
          <h2 style={{ fontFamily: 'monospace', fontSize: '1.5rem' }}>Race Intelligence Live</h2>
          <p style={{ color: '#888', marginTop: '0.5rem' }}>Reconnecting...</p>
          <button onClick={() => { this.setState({ hasError: false }); window.location.reload(); }}
            data-testid="error-boundary-reload" style={{ marginTop: '1rem', padding: '0.5rem 1.5rem',
              background: '#F97316', color: '#121212', border: 'none', borderRadius: '4px', cursor: 'pointer', fontWeight: 700 }}>
            Reload
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

// Auth context
export const useAuth = () => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const verifyToken = async () => {
      const storedToken = localStorage.getItem('token');
      if (storedToken) {
        try {
          const response = await axios.get(`${API}/auth/me`, {
            headers: { Authorization: `Bearer ${storedToken}` }
          });
          setUser(response.data);
          setToken(storedToken);
        } catch (error) {
          localStorage.removeItem('token');
          setToken(null);
          setUser(null);
        }
      }
      setLoading(false);
    };
    verifyToken();
  }, []);

  const login = async (username, password) => {
    try {
      const response = await axios.post(`${API}/auth/login`, { username, password });
      const { access_token, user: userData } = response.data;
      localStorage.setItem('token', access_token);
      setToken(access_token);
      setUser(userData);
      toast.success(`Welcome back, ${userData.username}!`);
      return true;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Login failed');
      return false;
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('demo_watermark');
    setToken(null);
    setUser(null);
    toast.info('Logged out successfully');
  };

  return { user, token, loading, login, logout };
};

// Protected route wrapper
const ProtectedRoute = ({ children, token, loading }) => {
  if (loading) {
    return (
      <div className="min-h-screen bg-[#121212] flex items-center justify-center">
        <div className="text-white font-heading text-xl">Loading...</div>
      </div>
    );
  }
  
  if (!token) {
    return <Navigate to="/login" replace />;
  }
  
  return children;
};

// Broadcast page wrapper with navigation
const BroadcastPageWrapper = ({ token }) => {
  const navigate = useNavigate();
  return <BroadcastOverlayPage token={token} onBack={() => navigate("/")} />;
};

// Login page wrapper
const LoginPageWrapper = ({ onLogin, token }) => {
  if (token) {
    return <Navigate to="/" replace />;
  }
  return <LoginPage onLogin={onLogin} />;
};

function App() {
  const { user, token, loading, login, logout } = useAuth();
  const [watermark, setWatermark] = useState(localStorage.getItem('demo_watermark') || '');

  const handleDemoAccess = (accessToken, watermarkText) => {
    setWatermark(watermarkText);
  };

  // Determine watermark text — demo viewers get their link watermark, users get their username, empty on login
  const watermarkText = watermark || (user ? `${user.username} · Authorised Access` : '');

  return (
    <ErrorBoundary>
    <div className="App min-h-screen bg-[#121212]">
      <Toaster 
        position="top-right" 
        toastOptions={{
          style: {
            background: '#1E1E1E',
            color: '#FFFFFF',
            border: '1px solid rgba(255,255,255,0.1)',
          },
        }}
      />
      <BrowserRouter>
        <ScreenProtection username={user?.username} watermarkText={watermarkText}>
          <Routes>
            <Route 
              path="/login" 
              element={<LoginPageWrapper onLogin={login} token={token} />} 
            />
            <Route 
              path="/demo/:token" 
              element={<SharedDemoPage onDemoAccess={handleDemoAccess} />} 
            />
            <Route 
              path="/auto-demo" 
              element={<GuidedTourPage token={token || localStorage.getItem('token')} />} 
            />
            <Route 
              path="/" 
              element={
                <ProtectedRoute token={token} loading={loading}>
                  <PresenterHubPage user={user} token={token} onLogout={logout} />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/racing" 
              element={
                <ProtectedRoute token={token} loading={loading}>
                  <RacingIntelligencePage user={user} token={token} onLogout={logout} />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/broadcast" 
              element={
                <ProtectedRoute token={token} loading={loading}>
                  <BroadcastPageWrapper token={token} />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/cross-sport" 
              element={
                <ProtectedRoute token={token} loading={loading}>
                  <CyclingVisionPage token={token} />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/library" 
              element={
                <ProtectedRoute token={token} loading={loading}>
                  <RaceLibraryPage user={user} token={token} onLogout={logout} />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/analytics" 
              element={
                <ProtectedRoute token={token} loading={loading}>
                  <DemoAnalyticsPage token={token} />
                </ProtectedRoute>
              } 
            />
          </Routes>
        </ScreenProtection>
      </BrowserRouter>
    </div>
    </ErrorBoundary>
  );
}

export default App;
